import type { JSONSchema7Definition } from "json-schema";
import { startCase } from "lodash";

import {
  FieldNode,
  InputKind,
  MapNode,
  ObjectNode,
  OneOfNode,
  OneOfOption,
  PrimitiveNode,
  Schema,
  UnionNode,
  ValueType,
} from "./types";

/*
 * Turns a JSON Schema into a tree of field nodes, once per schema object.
 * Every rule about the schema (refs, nullability, unions, defaults, input kinds) lives here,
 * so the field components only render what the node says.
 */

const DEFAULT_DISCRIMINATOR = "provider";

const compiled = new WeakMap<Schema, ObjectNode>();

const isSchema = (def: JSONSchema7Definition | undefined): def is Schema =>
  typeof def === "object" && def !== null;

const refName = (ref: string) => ref.split("/").pop() ?? ref;

const resolveRef = (ref: string, root: Schema): Schema => {
  const name = refName(ref);
  const target = root.$defs?.[name] ?? root.definitions?.[name];

  if (!isSchema(target)) {
    throw new Error(`SchemaForm: cannot resolve ${ref}`);
  }

  return target;
};

const isNullType = (def: JSONSchema7Definition) =>
  isSchema(def) && def.type === "null";

interface Resolved {
  schema: Schema;
  nullable: boolean;
  ref?: string;
  // Alternatives of a union that is not a union of objects (e.g. `string | string[]`)
  union?: Schema[];
}

const isNumeric = (def: Schema) =>
  def.type === "integer" || def.type === "number";

/*
 * Follows $ref and strips a `null` alternative, keeping the outer title, description and default.
 * Several alternatives become one number (`integer | number`), a union of objects (`oneOf`),
 * or a union of types that the form edits one type at a time.
 */
const resolve = (def: Schema, root: Schema): Resolved => {
  let schema = def;
  let nullable = false;
  let ref: string | undefined;

  if (Array.isArray(schema.type)) {
    nullable = schema.type.includes("null");
    const type = schema.type.find((item) => item !== "null");
    schema = { ...schema, type };
  }

  const alternatives = (schema.anyOf ?? []).filter(isSchema);
  if (alternatives.length > 0) {
    const nonNull = alternatives.filter((item) => !isNullType(item));
    nullable = nullable || nonNull.length < alternatives.length;

    if (nonNull.length === 1) {
      const inner = resolve(nonNull[0], root);
      return {
        schema: { ...inner.schema, title: undefined, ...withOuter(schema) },
        nullable: nullable || inner.nullable,
        ref: inner.ref,
      };
    }

    if (nonNull.every(isNumeric)) {
      const type = nonNull.every((item) => item.type === "integer")
        ? "integer"
        : "number";
      return { schema: { ...schema, anyOf: undefined, type }, nullable };
    }

    if (nonNull.every((item) => resolve(item, root).schema.properties)) {
      return {
        schema: { ...schema, anyOf: undefined, oneOf: nonNull },
        nullable,
      };
    }

    return {
      schema: { ...schema, anyOf: undefined },
      nullable,
      union: nonNull,
    };
  }

  if (schema.$ref) {
    ref = refName(schema.$ref);
    const inner = resolve(resolveRef(schema.$ref, root), root);
    return {
      schema: { ...inner.schema, title: undefined, ...withOuter(schema) },
      nullable: nullable || inner.nullable,
      ref: inner.ref ?? ref,
    };
  }

  return { schema, nullable, ref };
};

/*
 * The property keeps its own title, description and default over the referenced schema's.
 * A referenced schema's title is its class name, so it is dropped: a property without a title
 * is labelled by its key instead.
 */
const withOuter = (outer: Schema): Partial<Schema> => {
  const kept: Partial<Schema> = {};

  if (outer.title !== undefined) kept.title = outer.title;
  if (outer.description !== undefined) kept.description = outer.description;
  if (outer.default !== undefined) kept.default = outer.default;

  return kept;
};

const inputKind = (schema: Schema): InputKind => {
  if (schema.const !== undefined || schema.enum) return "select";
  if (schema.type === "boolean") return "switch";
  if (schema.type === "integer" || schema.type === "number") return "number";
  if (schema.format === "password") return "password";

  return "text";
};

const compilePrimitive = (
  base: Omit<PrimitiveNode, "kind" | "input" | "integer" | "options">,
  schema: Schema,
): PrimitiveNode => ({
  ...base,
  kind: "primitive",
  input: inputKind(schema),
  integer: schema.type === "integer",
  options:
    schema.const !== undefined
      ? [String(schema.const)]
      : (schema.enum ?? []).map(String),
  minimum: schema.minimum,
  maximum: schema.maximum,
});

const compileOneOf = (
  base: Omit<OneOfNode, "kind" | "discriminator" | "options">,
  schema: Schema,
  root: Schema,
): OneOfNode => {
  const discriminator =
    (schema as { discriminator?: { propertyName?: string } }).discriminator
      ?.propertyName ?? DEFAULT_DISCRIMINATOR;

  const options: OneOfOption[] = (schema.oneOf ?? [])
    .filter(isSchema)
    .map((alternative) => {
      const resolved = resolve(alternative, root);
      // The variant's own title (its class name) names the option
      const title = alternative.$ref
        ? resolveRef(alternative.$ref, root).title
        : alternative.title;
      const marker = resolved.schema.properties?.[discriminator];
      const value = isSchema(marker)
        ? String(marker.const ?? marker.enum?.[0] ?? title)
        : String(title ?? "option");

      // The union's default is not the variant's
      const node = compileObject(
        { ...base, title: title ?? base.title, default: undefined },
        resolved.schema,
        root,
        discriminator,
      );

      return { value, label: title ?? value, ref: resolved.ref, node };
    });

  // A scalar default names the default option; only an object default is a whole value
  const scalarDefault =
    base.default !== undefined &&
    base.default !== null &&
    typeof base.default !== "object";

  return {
    ...base,
    kind: "oneOf",
    discriminator,
    options,
    default: scalarDefault ? undefined : base.default,
    defaultOption: scalarDefault ? String(base.default) : undefined,
  };
};

const valueTypeOf = (schema: Schema): ValueType => {
  if (schema.properties) return "object";

  switch (schema.type) {
    case "string":
    case "number":
    case "integer":
    case "boolean":
    case "array":
      return schema.type;
    default:
      return "object";
  }
};

// Each alternative is compiled at the union's path with the union's title; the union shows the label
const compileUnion = (
  base: Omit<UnionNode, "kind" | "options">,
  alternatives: Schema[],
  root: Schema,
): UnionNode => ({
  ...base,
  kind: "union",
  options: alternatives.map((alternative) => ({
    type: valueTypeOf(resolve(alternative, root).schema),
    node: {
      ...compileNode(base.key, alternative, base.path, false, root),
      title: base.title,
      description: undefined,
    },
  })),
});

const compileObject = (
  base: Omit<ObjectNode, "kind" | "children">,
  schema: Schema,
  root: Schema,
  skipKey?: string,
): ObjectNode => {
  const required = schema.required ?? [];

  const children = Object.entries(schema.properties ?? {})
    .filter(([key, def]) => key !== skipKey && isSchema(def))
    .map(([key, def]) =>
      compileNode(
        key,
        def as Schema,
        base.path ? `${base.path}.${key}` : key,
        required.includes(key),
        root,
      ),
    );

  return { ...base, kind: "object", children };
};

const compileNode = (
  key: string,
  def: Schema,
  path: string,
  required: boolean,
  root: Schema,
): FieldNode => {
  const { schema, nullable, union } = resolve(def, root);

  const base = {
    path,
    key,
    title: schema.title ?? startCase(key),
    description: schema.description,
    nullable,
    required,
    default: schema.default,
  };

  if (union) {
    return compileUnion(base, union, root);
  }

  if (schema.oneOf) {
    return compileOneOf(base, schema, root);
  }

  if (schema.type === "array") {
    const item =
      !Array.isArray(schema.items) && isSchema(schema.items)
        ? schema.items
        : {};
    return {
      ...base,
      kind: "array",
      item: compileNode("", item, path, false, root),
    };
  }

  if (schema.properties) {
    return compileObject(base, schema, root);
  }

  if (schema.type === "object" || isSchema(schema.additionalProperties)) {
    const valueSchema = isSchema(schema.additionalProperties)
      ? resolve(schema.additionalProperties, root).schema
      : { type: "string" as const };

    const value = compilePrimitive(
      {
        path,
        key: "",
        title: "",
        nullable: false,
        required: false,
        default: valueSchema.default,
        minimum: valueSchema.minimum,
        maximum: valueSchema.maximum,
      },
      valueSchema,
    );

    const node: MapNode = { ...base, kind: "map", value };
    return node;
  }

  return compilePrimitive(base, schema);
};

export const compileSchema = (schema: Schema): ObjectNode => {
  const cached = compiled.get(schema);
  if (cached) return cached;

  const root = compileObject(
    {
      path: "",
      key: "",
      title: schema.title ?? "",
      description: schema.description,
      nullable: false,
      required: true,
      default: schema.default,
    },
    schema,
    schema,
  );

  compiled.set(schema, root);
  return root;
};
